<script>
  import { LayerCake, Svg } from "layercake";
  import Bar from "./Bar.svelte";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import Trendline from "./Trendline.svelte";
  import { useData } from "$utils/data.svelte.js";
  import { percentFormat } from "$utils/formats.js";
  import { scaleBand } from "d3";
  import Button from "./Button.svelte";
  import IconRight from "./IconRight.svelte";
  import IconLeft from "./IconLeft.svelte";

  /**@type {{dataFilter:string, showAMetric:boolean}} */
  let { dataFilter = "", showAMetric = true } = $props();

  let toolData = useData();
  let myData = $derived(toolData.isReady() ? toolData.getGroupData(dataFilter) : []);
  let currentMetrics = $derived(toolData.metricsToShow[toolData.visibleMetricIndex]);

  function getMetricData(metric) {
    return myData.filter((d) => d.metric == metric && d.type != "change");
  }
  function getChangeData(metric) {
    return myData.filter((d) => d.metric == metric && d.type == "change");
  }
</script>

{#if toolData.isReady()}
  <div class="legend-line">
    {#if showAMetric}
      <div class="legend-pair">
        <div class="legend-color" style:background-color={toolData.getGroupColor(dataFilter)}></div>
        <p class="legend-title">{dataFilter} Microbusinesses</p>
      </div>
    {/if}
    <div class="legend-pair">
      <div class="legend-color" style:background-color={urbanColors.space_gray_shade_lighter}></div>
      <p class="legend-title">Full sample {showAMetric ? '': 'of businesses'}</p>
    </div>
  </div>
  <div class="chart-container">
    {#each currentMetrics as metric, i}
      <div class="metric-container">
        <p class="metric-title">{metric}</p>
        <div class="viz-container">
          <LayerCake
            padding={{ right: 50, left: 60, top: 10, bottom: 35 }}
            x={"type"}
            y={"value"}
            z={"category"}
            xDomain={[0, 1]}
            yDomain={toolData.getMinMax(metric)}
            zDomain={[dataFilter, "Median"]}
            data={getMetricData(metric)}
            custom={{
              seriesColors: [
                urbanColors.space_gray_shade_lighter,
                showAMetric ? toolData.getGroupColor(dataFilter) : "transparent"
              ],
              seriesWidths: [4, 7],
              format: toolData.getMetricFormat(metric)
            }}
          >
            <Svg>
              <Trendline labeled={true} labels={["Preloan", "Postloan"]} />
            </Svg>
          </LayerCake>

          <LayerCake
            padding={{ bottom: 48, top: 25, left: 0, right: 35 }}
            x={"value"}
            y={"category"}
            yDomain={[dataFilter, "Median"]}
            xDomain={toolData.getChangeMinMax(metric)}
            yScale={scaleBand().paddingInner(0.2)}
            data={getChangeData(metric)}
            custom={{
              seriesColors: [
                urbanColors.space_gray_shade_lighter,
                showAMetric ? toolData.getGroupColor(dataFilter) : "transparent"
              ],
              format: percentFormat,
              xKey: "value"
            }}
          >
            <Svg>
              <Bar labeled={true} label="Percent change" />
            </Svg>
          </LayerCake>
        </div>
      </div>
    {/each}
  </div>
  <div class="chart-swap-button">
    <Button
      variant="primary-gray"
      size="small"
      iconPosition={toolData.visibleMetricIndex == 0 ? "right" : "left"}
      onclick={() => {
        toolData.swapVisibleMetricSet();
      }}
      >Show more metrics ({toolData.visibleMetricIndex + 1}/2)
      {#snippet icon(iconColor)}
        {#if toolData.visibleMetricIndex == 0}
          <IconRight size={16} fill={"black"} />
        {:else}
          <IconLeft size={16} fill={"black"} />
        {/if}
      {/snippet}
    </Button>
  </div>
{/if}

<style>
  .legend-line {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
  }
  .legend-pair {
    display: flex;
    gap: 10px;
    align-items: center;
    p {
      margin: 0;
      font-size: var(--font-size-normal);
    }
  }
  .legend-color {
    border-radius: 12px;
    width: 12px;
    height: 12px;
  }
  .chart-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px 60px;
    @media screen and (max-width: 540px) {
      grid-template-columns: 1fr;
      gap: 0px;
    }
  }
  .metric-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    @media screen and (max-width: 540px) {
      padding-bottom: 36px;
    }
  }
  .viz-container {
    height: 120px;
    display: grid;
    grid-template-columns: 2.5fr 1fr;
    gap: 20px;
  }

  p.metric-title {
    margin: 0 0 10px 0;
    font-style: italic;
    font-weight: 600;
    line-height: 162.5%;
  }
  .chart-swap-button {
    margin-top: 30px;
    display: flex;
    justify-content: center;
    @media screen and (max-width: 1160px) {
      justify-content: center;
      margin-bottom: 20px;
    }
    @media screen and (max-width: 540px){
      margin-top: 0;
    }
  }
</style>
